<template>
  <v-container v-if="!showOfficeManagement" class="text-center" id="page">
    <img :src="logoUrl" alt="CleverReach Logo" class="cleverreach-logo" />
    <div>
      <h1>Willkommen im CleverReach Analyse Tool, {{ username }} !</h1>
      <br />
      <div v-if="showProjects">
        <h2>Deine Projekte:</h2>
        <br />
        <v-btn
          v-for="project in projects"
          :key="project.id"
          @click="selectProject(project)"
          rounded="xl"
          size="x-large"
          elevation="10"
          color="success"
          variant="tonal"
          block
          class="mb-3 project-button"
        >
          <div class="flex justify-space-between align-center w-100 px-4">
            <p class="flex-grow-1">{{ project.name }}</p>
            <v-icon
              v-auth-click="{
                roles: authStore.isLicenceManager || authStore.isSimpleUser,
                message:
                  'Sie haben keine Berechtigung, dieses Projekt zu löschen!',
              }"
              color="red"
              @click.stop="deleteSelectedProject(project.id)"
              class="ml-15"
              >mdi-close-circle</v-icon
            >
          </div>
        </v-btn>
      </div>
      <div v-if="showCompanies">
        <h2>Deine Organisationen:</h2>
        <br />
        <v-btn
          v-for="company in companies"
          :key="company.id"
          @click="selectCompany(company)"
          rounded="xl"
          size="x-large"
          elevation="10"
          color="success"
          variant="tonal"
          block
          class="mb-3 project-button"
        >
          <div class="flex justify-space-between align-center w-100 px-4">
            <p class="flex-grow-1">{{ company.name }}</p>
            <v-icon
              v-auth-click="{
                roles: authStore.isLicenceManager || authStore.isSimpleUser,
                message:
                  'Sie haben keine Berechtigung, diese Organisation zu löschen!',
              }"
              color="red"
              @click.stop="deleteSelectedCompany(company.id)"
              class="ml-15"
              >mdi-close-circle</v-icon
            >
          </div>
        </v-btn>
      </div>
      <div v-if="showOffices">
        <h2>Deine Standorte:</h2>
        <br />
        <v-btn
          v-for="office in offices"
          :key="office.office_id"
          @click="selectOffice(office)"
          rounded="xl"
          size="x-large"
          elevation="10"
          color="success"
          variant="tonal"
          block
          class="mb-3 project-button"
        >
          <div class="flex justify-space-between align-center w-100 px-4">
            <p class="flex-grow-1">{{ office.office_name }}</p>
            <v-icon
              v-auth-click="{
                roles: authStore.isLicenceManager || authStore.isSimpleUser,
                message:
                  'Sie haben keine Berechtigung, diesen Standort zu löschen!',
              }"
              color="red"
              @click.stop="deleteSelectedOffice(office.office_id)"
              class="ml-15"
              >mdi-close-circle</v-icon
            >
          </div>
        </v-btn>
      </div>
      <br />
      <br />
      <div>
        <v-btn
          @click="startNewProject"
          class="project-button"
          rounded="xl"
          size="x-large"
          elevation="10"
          color="success"
          variant="tonal"
          block
          >Starte neues Projekt</v-btn
        >
      </div>
      <br />
      <div>
        <v-container>
          <v-btn
            @click="reset"
            class="project-button mr-5"
            rounded="xl"
            size="large"
            elevation="10"
            variant="tonal"
            >Zurück</v-btn
          >
          <v-btn
            @click="goToUserProfile"
            class="project-button"
            rounded="xl"
            size="large"
            elevation="10"
            variant="tonal"
            >Nutzerprofil bearbeiten</v-btn
          >
        </v-container>
      </div>
    </div>
  </v-container>
  <v-container>
    <the-office-management v-if="showOfficeManagement"></the-office-management>
  </v-container>
  <div class="text-center">
    <v-dialog v-model="officeSelected" width="600px">
      <v-card style="border-radius: 15px">
        <v-card-text
          >Standort zum Projekt '{{ selectedProject?.name }}' erfolgreich
          ausgewählt. Du kannst jetzt in den Karten-Editor oder den
          CleverRoute-Import wechseln. Falls Du Änderungen an den Standortdaten,
          Schichten etc. vornehmen möchtest oder neue Adressen hinzufügen
          möchtest, gehe bitte zur Projekt-Verwaltung.
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            @click="officeSelected = false"
            rounded="xl"
            elevation="10"
            variant="tonal"
            >Schließen</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="deleteSelected"
      width="auto"
      style="flex: auto; justify-content: center; align-items: center"
      v-if="
        authStore.hasRole('simple-user') || authStore.hasRole('licence-manager')
      "
    >
      <v-card style="border-radius: 15px">
        <v-card-text>
          Mit Bestätigung des Löschvorgangs werden alle Daten - Standorte,
          Adressen, CR-Ergebnisse - zu
          {{ deleteItemName ? `"${deleteItemName}"` : "diesem Element" }}
          unwiderruflich gelöscht.
          {{
            deleteType === "project"
              ? "Projekt"
              : deleteType === "company"
                ? "Organisation"
                : "Standort"
          }}
          wirklich löschen?
        </v-card-text>
        <v-card-actions>
          <v-btn
            @click="confirmDelete"
            style="margin-right: auto"
            rounded="xl"
            size="large"
            elevation="10"
            color="warning"
            variant="tonal"
            >Ja, löschen!</v-btn
          >
          <v-btn
            @click="deleteSelected = false"
            style="margin-left: auto"
            rounded="xl"
            size="large"
            elevation="10"
            >Abbrechen</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { useOfficeStore } from "../store/officeStore";
import { useAuthStore } from "../store/authStore";
import TheOfficeManagement from "./TheOfficeManagement.vue";
import { Company, Offices, OfficeApiView, Project } from "../api/api";
import "@mdi/font/css/materialdesignicons.css";
import cleverReachLogo from "../assets/cleverReachLogo.png";

export default {
  components: {
    TheOfficeManagement,
  },
  props: {
    setResetHomeFunction: {
      type: Function,
      default: null,
    },
  },
  setup() {
    const authStore = useAuthStore();
    const officeStore = useOfficeStore();

    return {
      authStore,
      officeStore,
    };
  },
  data() {
    return {
      selectedProjectId: 0,
      selectedCompanyId: 0,
      selectedOfficeId: 0,
      selectedProject: null as Project | null, // currently selected project
      selectedCompany: null as Company | null, // currently selected company
      selectedOffice: null as Offices | null, // currently selected office
      showProjects: true,
      showCompanies: false,
      showOffices: false,
      showOfficeManagement: false,
      projectCompanies: [],
      projectOffices: [],
      officeSelected: false,
      deleteSelected: false,
      logoUrl: cleverReachLogo,
      deleteType: "project", // Can be 'project', 'company', or 'office'
      deleteItemName: "", // Name of the item being deleted
    };
  },
  computed: {
    username() {
      return this.authStore?.user?.username || "";
    },
    projects(): Project[] {
      // @ts-ignore
      return this.officeStore.getProjects;
    },
    offices(): OfficeApiView[] {
      // @ts-ignore
      return this.officeStore.getOffices;
    },
    companies(): Company[] {
      return this.officeStore.getCompanies;
    },
  },
  methods: {
    async selectProject(project: any) {
      // Reset store values
      this.officeStore.selectedCompany = null;
      this.officeStore.selectedOffice = null;

      // Set new project
      this.officeStore.selectProject(project);
      console.log("Selected project:", project);
      await this.officeStore.fetchCompanies(project.id);
      this.selectedProject = project;
      this.selectedProjectId = project.id;
      this.showCompanies = true;
      this.showProjects = false;
    },
    async selectCompany(company: any) {
      // console.log("Selecting company:", company);
      this.officeStore.selectCompany(company);
      // console.log("Store after company selection:", {
      //   project: this.officeStore.getSelectedProject,
      //   company: this.officeStore.getSelectedCompany,
      //   office: this.officeStore.getSelectedOffice,
      // });
      await this.officeStore.fetchOffices(this.selectedProjectId, company.id);
      this.selectedCompany = company;
      this.selectedCompanyId = company.id;
      this.showOffices = true;
      this.showCompanies = false;
    },
    async selectOffice(office: any) {
      // console.log("Selecting office:", office);
      this.officeSelected = true;
      this.officeStore.selectOffice(office);
      // console.log("Store after office selection:", {
      //   project: this.officeStore.getSelectedProject,
      //   company: this.officeStore.getSelectedCompany,
      //   office: this.officeStore.getSelectedOffice,
      // });
      this.selectedOffice = office as Offices;
      this.selectedOfficeId = office.officeId;
      console.log("Selected office ID in selectOffice:", this.selectedOfficeId);
    },
    startNewProject() {
      this.showOfficeManagement = true;
    },
    goToUserProfile() {
      // logic to go to user profile
    },
    reset() {
      // Step-by-step back navigation
      if (this.showOffices) {
        // If showing offices, go back to companies
        this.showOffices = false;
        this.showCompanies = true;
        this.selectedOffice = null;
        this.selectedOfficeId = 0;
        this.officeStore.selectedOffice = null;
      } else if (this.showCompanies) {
        // If showing companies, go back to projects
        this.showCompanies = false;
        this.showProjects = true;
        this.selectedCompany = null;
        this.selectedCompanyId = 0;
        this.officeStore.selectedCompany = null;
      } else if (this.showOfficeManagement) {
        // If in office management, go back to projects
        this.showOfficeManagement = false;
        this.showProjects = true;
      } else {
        // If at projects level or any other state, do a partial reset
        // Reset selection state but keep the projects data
        this.officeStore.selectedProject = null;
        this.officeStore.selectedCompany = null;
        this.officeStore.selectedOffice = null;

        this.showProjects = true;
        this.showCompanies = false;
        this.showOffices = false;
        this.showOfficeManagement = false;
        this.selectedProject = null;
        this.selectedProjectId = 0;
        this.selectedCompanyId = 0;
        this.selectedOfficeId = 0;
        this.officeSelected = false;
      }
    },
    resetToProjectList() {
      // Direct reset to project list (used by Home button)
      // Reset all selection state and show project list
      this.officeStore.selectedProject = null;
      this.officeStore.selectedCompany = null;
      this.officeStore.selectedOffice = null;

      this.showProjects = true;
      this.showCompanies = false;
      this.showOffices = false;
      this.showOfficeManagement = false;
      this.selectedProject = null;
      this.selectedProjectId = 0;
      this.selectedCompanyId = 0;
      this.selectedOfficeId = 0;
      this.officeSelected = false;
    },
    deleteSelectedProject(projectId: number) {
      this.selectedProjectId = projectId;
      this.deleteType = "project";
      // console.log("Deleting selected project with id: ", projectId);
      const project = this.projects.find((p) => p.id === projectId);
      this.deleteItemName = project?.name || "";
      this.deleteSelected = true;
    },
    deleteSelectedCompany(companyId: number) {
      this.selectedCompanyId = companyId;
      this.deleteType = "company";
      const company = this.companies.find((c) => c.id === companyId);
      this.deleteItemName = company?.name || "";
      this.deleteSelected = true;
    },
    deleteSelectedOffice(officeId: number) {
      this.selectedOfficeId = officeId;
      this.deleteType = "office";
      const office = this.offices.find((o) => o.officeId === officeId);
      this.deleteItemName = office?.officeName || "";
      this.deleteSelected = true;
    },
    async confirmDelete() {
      if (this.deleteType === "project") {
        await this.deleteProject(this.selectedProjectId);
      } else if (this.deleteType === "company") {
        await this.deleteCompany(this.selectedCompanyId);
      } else if (this.deleteType === "office") {
        await this.deleteOffice(this.selectedOfficeId);
      }
      this.deleteSelected = false;
    },
    async deleteProject(projectId: number) {
      console.log("Deleting project with id: ", projectId);

      await this.officeStore.fetchCompanies(projectId);
      let projComps = this.officeStore.getCompanies;
      console.log("Companies to delete: ", projComps);

      for (const comp of projComps as Company[]) {
        if (comp.id !== undefined) {
          await this.officeStore.fetchOffices(projectId, comp.id);
          let projOffs = this.officeStore.getOffices;
          console.log("Offices to delete: ", projOffs);
          for (const office of projOffs as Offices[]) {
            if (office.office_id !== undefined) {
              await this.deleteOffice(office.office_id);
            }
          }
          // Before deleting the company, delete the user_company_relation
          await this.officeStore.deleteUserCompanyRelation(comp.id);
          await this.officeStore.deleteCompany(comp.id, projectId);
        }
      }
      await this.officeStore.deleteProjectUserRelation(projectId);

      await this.officeStore.deleteProject(projectId);
      this.deleteSelected = false;
    },
    async deleteCompany(companyId: number) {
      console.log("Deleting company with id: ", companyId);
      // Fetch and delete all offices first
      console.log("Selected project id: ", this.selectedProjectId);
      await this.officeStore.fetchOffices(this.selectedProjectId, companyId);
      let companyOffices = this.officeStore.getOffices;
      console.log("Company Offices: ", companyOffices);
      for (const office of companyOffices as Offices[]) {
        if (office.office_id !== undefined) {
          await this.deleteOffice(office.office_id);
        }
      }
      // Before deleting the company, delete the user_company_relation
      await this.officeStore.deleteUserCompanyRelation(companyId);

      // Delete the company
      await this.officeStore.deleteCompany(companyId, this.selectedProjectId);

      // Refresh the company list
      await this.officeStore.fetchCompanies(this.selectedProjectId);
    },
    async deleteOffice(officeId: number) {
      console.log("Deleting office with id:", officeId);

      try {
        // First, get the address IDs related to this office
        const addressIds = await this.getAddressIdsForOffice(officeId);
        console.log("Found address IDs to delete:", addressIds);

        // Check which addresses are used by other offices before deleting anything
        let addressesToDelete = [];

        if (addressIds.length > 0) {
          // Check if these addresses are used by other offices
          const addressesUsedByOthers =
            await this.officeStore.checkAddressesUsedByOtherOffices(
              addressIds,
              officeId,
            );

          console.log(
            "Addresses used by other offices:",
            addressesUsedByOthers,
          );

          // Filter out addresses used by other offices
          addressesToDelete = addressIds.filter(
            (id) => !addressesUsedByOthers.includes(id),
          );

          console.log("Addresses safe to delete:", addressesToDelete);
        }

        // Before deleting the office, delete all paths for this office
        const pathsToDelete = await this.getPathsForOfficeToDelete(
          officeId,
          addressIds,
        );

        console.log("Paths to delete:", pathsToDelete);
        await this.officeStore.deletePathsForOffice(pathsToDelete);

        // Now delete the office itself first
        console.log(
          "Deleting office with id:",
          officeId,
          "from offices: ",
          this.offices,
        );
        const office = this.offices.find((o) => o.office_id === officeId);
        console.log("Found Office to delete:", office);
        if (office && office.office_id !== undefined) {
          console.log("Reached Line 460!");
          await this.officeStore.deleteOffice(
            this.selectedProjectId,
            this.selectedCompanyId,
            office.office_id,
          );

          // Refresh the office list
          await this.officeStore.fetchOffices(
            this.selectedProjectId,
            this.selectedCompanyId,
          );

          // After deleting the office,
          // now we can delete the addresses that aren't used by other offices
          if (addressesToDelete.length > 0) {
            console.log("Now deleting unused addresses:", addressesToDelete);
            await this.deleteAddressesBatch(addressesToDelete);
          }
        }
      } catch (error) {
        console.error("Error deleting office:", error);
      }
    },
    // Method to get paths for an office to delete
    async getPathsForOfficeToDelete(
      officeId: number,
      addressIds: number[],
    ): Promise<number[]> {
      const response = await this.officeStore.fetchPaths(officeId, addressIds);
      console.log("Paths for office with id:", officeId, "are:", response);
      let pathsToDelete = [];
      if (response && Array.isArray(response)) {
        for (const item of response) {
          pathsToDelete.push(item.id);
        }
      }
      return pathsToDelete;
    },
    // Method to get address IDs for an office from PathsView
    async getAddressIdsForOffice(officeId: number): Promise<number[]> {
      try {
        // Assume you have a method in your store or API to get the address IDs
        // from PathsView table based on office_id
        const response = await this.officeStore.fetchPaths(officeId);
        console.log("response in getAddressIdsForOffice =", response);
        // Extract origin_ids from the response data array
        const addressIds = [];
        if (response && Array.isArray(response)) {
          for (const item of response) {
            if (item && item.origin_id) {
              addressIds.push(item.origin_id);
            }
          }
        }
        return addressIds;
      } catch (error) {
        console.error("Error fetching address IDs:", error);
        return [];
      }
    },
    // Batch deletion method
    async deleteAddressesBatch(addressIds: number[]): Promise<void> {
      if (addressIds.length === 0) return;

      try {
        await this.officeStore.deleteAddressesBatch(addressIds);
        console.log(
          `Successfully deleted ${addressIds.length} addresses in batch`,
        );
      } catch (error) {
        console.error("Error batch deleting addresses:", error);
      }
    },
    // Individual address deletion method
    async deleteAddress(addressId: number): Promise<void> {
      try {
        await this.officeStore.deleteAddress(addressId);
        console.log(`Successfully deleted address ID: ${addressId}`);
      } catch (error) {
        console.error(`Error deleting address ID ${addressId}:`, error);
      }
    },
  },
  async created() {
    this.selectedProjectId = 0;
    this.selectedCompanyId = 0;
    this.selectedOfficeId = 0;

    // Register reset function with parent component (direct to project list)
    if (this.setResetHomeFunction) {
      this.setResetHomeFunction(this.resetToProjectList);
    }

    try {
      await this.officeStore.fetchProjects();
    } catch (error) {
      console.error("Failed to load projects:", error);
      // Projects will show as empty array, which is handled by the template
    }
  },
};
</script>

<style scoped>
.cleverreach-logo {
  width: 80px; /* Adjust this value as needed */
  height: auto;
  margin-left: 20px; /* Adjust this value to increase/decrease left margin */
  margin-top: 10px; /* Optional: Adds some top margin */
}

#page {
  display: flex;
  justify-content: center;
  align-items: self-start;
  min-height: 100vh;
  background-image: linear-gradient(
      rgba(255, 255, 255, 0.75),
      rgba(255, 255, 255, 0.65)
    ),
    url(https://demo.cleverroute.net/hero.jpg);
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  padding-bottom: 50px;
}
.project-button {
  backdrop-filter: blur(2px);
  background-color: rgba(255, 255, 255, 0.85) !important;
}
.project-button .flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
.project-button .ml-auto {
  margin-left: auto;
}
</style>
